import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Spinner } from "@fluentui/react-components";
import { AppShell } from "@/app/AppShell";
import { ROUTES } from "@/routes";

// ── Lazy page imports (all sourced from feature index.ts) ─────────────────────

const CollectionsPage = lazy(() =>
  import("@/features/collections").then((m) => ({ default: m.CollectionsLanding }))
);
const ObligorDetailPage = lazy(() =>
  import("@/features/collections").then((m) => ({ default: m.ObligorDetail }))
);
const RevenueForecastingPage = lazy(() =>
  import("@/features/revenue-forecasting").then((m) => ({ default: m.ForecastingLanding }))
);
const FraudPage = lazy(() =>
  import("@/features/fraud").then((m) => ({ default: m.FraudLanding }))
);
const AiAssistantsPage = lazy(() =>
  import("@/features/ai-assistants").then((m) => ({ default: m.AiAssistantsPage }))
);

const SettingsLayout = lazy(() =>
  import("@/features/settings").then((m) => ({ default: m.SettingsLayout }))
);
const AssistantsManagementPage = lazy(() =>
  import("@/features/settings").then((m) => ({ default: m.AssistantsManagementPage }))
);
const KnowledgeBasePage = lazy(() =>
  import("@/features/settings").then((m) => ({ default: m.KnowledgeBasePage }))
);
const KnowledgeBaseDetailPage = lazy(() =>
  import("@/features/settings").then((m) => ({ default: m.KnowledgeBaseDetailPage }))
);
const RoleManagementPage = lazy(() =>
  import("@/features/settings").then((m) => ({ default: m.RoleManagementRoute }))
);

// ── Fallback spinner ──────────────────────────────────────────────────────────

const Fallback = (): React.JSX.Element => (
  <div style={{ padding: "2rem" }}>
    <Spinner label="Loading..." />
  </div>
);

// ── Router ────────────────────────────────────────────────────────────────────

export function AppRouter(): React.JSX.Element {
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to={ROUTES.collections} replace />} />

          <Route path={ROUTES.collections}       element={<CollectionsPage />} />
          <Route path={ROUTES.collectionsDetail} element={<ObligorDetailPage />} />
          <Route path={`${ROUTES.revenueForecastingRoot}/*`} element={<RevenueForecastingPage />} />
          <Route path={ROUTES.fraud}             element={<FraudPage />} />

          {/* AI Assistants — end-user chat + docs experience */}
          <Route path={ROUTES.aiAssistants} element={<AiAssistantsPage />} />

          {/* Settings — SettingsLayout provides the tab strip; Outlet renders the active sub-page */}
          <Route path={ROUTES.settings} element={<SettingsLayout />}>
            <Route index element={<Navigate to={ROUTES.settingsAssistants} replace />} />
            <Route path={ROUTES.settingsAssistants}          element={<AssistantsManagementPage />} />
            <Route path={ROUTES.settingsKnowledgeBase}       element={<KnowledgeBasePage />} />
            <Route path={ROUTES.settingsKnowledgeBaseDetail} element={<KnowledgeBaseDetailPage />} />
            <Route path={ROUTES.settingsPermissions}         element={<RoleManagementPage />} />
          </Route>

          {/* Legacy redirect */}
          <Route path="/roles" element={<Navigate to={ROUTES.settingsPermissions} replace />} />

          <Route path="*" element={<Navigate to={ROUTES.collections} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
